import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import loadingAnimation from './animations/loading-animation.json';
import Dashboard from './components/Dashboard';
import Calendar from './components/Calendar';
import HoursSummaryPage from './components/HoursSummaryPage';
import Employees from './components/Employees';
import Requests from './components/Requests';
import SuccessPage from './components/SuccessPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 MANAGER SOFTWARE: React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-screen">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <h2>Something went wrong</h2>
            <p>The application encountered an unexpected error.</p>
            <div className="error-actions">
              <button 
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="retry-button"
              >
                Reload Application
              </button>
              <button 
                onClick={() => window.location.href = 'https://calendar.joinoptivo.com'}
                className="calendar-button"
              >
                Go to Calendar
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple Router Component - no React Router needed
function SimpleRouter({ children, currentPath, setCurrentPath }) {
  // Listen to browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [setCurrentPath]);

  return <div className="app-container">{children}</div>;
}

// Main App Content Component
function AppContent() {
  // Debug: Confirm this is the MANAGER software
  console.log('🚀🚀🚀 MANAGER SOFTWARE LOADED - URL:', window.location.href);
  console.log('🚀🚀🚀 MANAGER SOFTWARE LOADED - Search params:', window.location.search);
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Helper function to get cookie value
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Simple navigation function
  const navigateTo = (path) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };

  // Handle navigation after authentication
  useEffect(() => {
    if (isAuthenticated && !isLoading && authChecked && currentPath === '/') {
      console.log('🧭 MANAGER SOFTWARE: Navigating to dashboard...');
      navigateTo('/dashboard');
    }
  }, [isAuthenticated, isLoading, authChecked, currentPath]);

  useEffect(() => {
    // Only run auth check once
    if (authChecked) return;
    
    const checkAuth = async () => {
      console.log('🔍🔍🔍 MANAGER SOFTWARE: Starting authentication check...');
      setIsLoading(true);
      setHasError(false);
      
      try {
        // Check for token in URL parameters first
        const urlParams = new URLSearchParams(window.location.search);
        let token = urlParams.get('token');
        
        console.log('🔍��🔍 MANAGER SOFTWARE: URL search params:', window.location.search);
        console.log('🔍🔍🔍 MANAGER SOFTWARE: Token from URL:', token ? 'TOKEN FOUND' : 'NO TOKEN');
        
        if (token) {
          console.log('📝📝📝 MANAGER SOFTWARE: Token found in URL parameters - storing...');
          localStorage.setItem('token', token);
          // Clean URL without causing navigation issues
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Check localStorage, sessionStorage, and cookies
          token = localStorage.getItem('token') || 
                  sessionStorage.getItem('token') || 
                  getCookie('token');
          
          if (token) {
            console.log('📝📝📝 MANAGER SOFTWARE: Token found in storage');
          } else {
            console.log('❌❌❌ MANAGER SOFTWARE: No token found, user needs to authenticate');
            setIsAuthenticated(false);
            setIsLoading(false);
            setAuthChecked(true);
            return;
          }
        }

        // Use environment variable for API base URL
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
        console.log('🔗🔗🔗 MANAGER SOFTWARE: Using API URL:', API_BASE_URL);

        // Validate token with backend
        console.log('🔐🔐🔐 MANAGER SOFTWARE: Validating token with backend...');
        const response = await fetch(`${API_BASE_URL}/api/auth/validate`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          console.log('✅✅✅ MANAGER SOFTWARE: Token validation successful');
          setIsAuthenticated(true);
          localStorage.setItem('token', token);
        } else if (response.status === 401) {
          console.log('🔄🔄🔄 MANAGER SOFTWARE: Token expired, attempting refresh...');
          // Try to refresh token
          const refreshResponse = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            console.log('✅✅✅ MANAGER SOFTWARE: Token refresh successful');
            localStorage.setItem('token', refreshData.token);
            setIsAuthenticated(true);
          } else {
            console.log('❌❌❌ MANAGER SOFTWARE: Token refresh failed');
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
            setIsAuthenticated(false);
          }
        } else {
          console.log('❌❌❌ MANAGER SOFTWARE: Token validation failed');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('💥💥💥 MANAGER SOFTWARE: Authentication error:', error);
        setHasError(true);
        setIsAuthenticated(false);
      } finally {
        console.log('🏁🏁🏁 MANAGER SOFTWARE: Authentication check complete, setting loading to false');
        setIsLoading(false);
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, [authChecked]);

  // Listen for unauthorized events from other components
  useEffect(() => {
    const handleUnauthorized = (event) => {
      console.log('Manager: Received unauthorized event:', event.detail);
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setIsAuthenticated(false);
    window.location.href = 'https://calendar.joinoptivo.com';
  };

  const handleRetry = () => {
    setHasError(false);
    setAuthChecked(false); // This will trigger the auth check again
  };

  // Show loading screen
  if (isLoading) {
    return (
      <div className="loading-screen">
        <Lottie 
          animationData={loadingAnimation} 
          style={{ width: 200, height: 100 }}
          loop={true}
        />
      </div>
    );
  }

  // Show error screen
  if (hasError) {
    return (
      <div className="error-screen">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2>Connection Error</h2>
          <p>Unable to connect to the server. Please check your internet connection and try again.</p>
          <div className="error-actions">
            <button 
              onClick={handleRetry}
              className="retry-button"
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.href = 'https://calendar.joinoptivo.com'}
              className="calendar-button"
            >
              Go to Calendar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Simple routing without React Router
  const renderCurrentPage = () => {
    if (!isAuthenticated) {
      return (
        <div className="auth-redirect">
          <div className="auth-content">
            <h2>Manager Access Required</h2>
            <p>You need to authenticate as a manager to access this dashboard.</p>
            <button 
              onClick={() => window.location.href = 'https://calendar.joinoptivo.com'}
              className="calendar-button"
            >
              Go to Calendar to Login
            </button>
          </div>
        </div>
      );
    }

    // Show redirect message while navigating to dashboard
    if (currentPath === '/' && isAuthenticated) {
      return (
        <div className="redirect-message">
          <Lottie 
            animationData={loadingAnimation} 
            style={{ width: 200, height: 100 }}
            loop={true}
          />
        </div>
      );
    }

    switch (currentPath) {
      case '/dashboard':
        return <Dashboard onLogout={handleLogout} />;
      case '/calendar':
        return <Calendar />;
      case '/employees':
        return <Employees />;
      case '/requests':
        return <Requests />;
      case '/hours-summary':
        return <HoursSummaryPage />;
      case '/success':
        return <SuccessPage />;
      default:
        return <Dashboard onLogout={handleLogout} />;
    }
  };

  return (
    <SimpleRouter currentPath={currentPath} setCurrentPath={setCurrentPath}>
      {renderCurrentPage()}
    </SimpleRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}

export default App; 